import { useEffect, useState } from 'react'

// Constants matching theme values
const LEFT_MENU_WIDTH_COLLAPSED = 57
const LEFT_MENU_WIDTH_EXPANDED = 290
const MOBILE_BREAKPOINT = 960 // md breakpoint in pixels

/**
 * Hook to track the current left menu width based on its open/closed state.
 * Listens to window resize events to detect menu state changes.
 *
 * @returns Current left menu width in pixels
 */
export const useLeftMenuWidth = (): number => {
  const [leftMenuWidth, setLeftMenuWidth] = useState(() => {
    // Check if mobile on initial render
    if (window.innerWidth < MOBILE_BREAKPOINT) return 0

    // Check initial DOM state
    const drawer = document.querySelector(
      '[class*="LeftMenu-drawerOpen"], [class*="LeftMenu-drawerClose"]',
    )
    if (!drawer) return LEFT_MENU_WIDTH_COLLAPSED

    const isOpen = drawer.classList.toString().includes('drawerOpen')
    return isOpen ? LEFT_MENU_WIDTH_EXPANDED : LEFT_MENU_WIDTH_COLLAPSED
  })

  useEffect(() => {
    const updateMenuWidth = () => {
      const isMobile = window.innerWidth < MOBILE_BREAKPOINT

      if (isMobile) {
        setLeftMenuWidth(0)
        return
      }

      const drawer = document.querySelector(
        '[class*="LeftMenu-drawerOpen"], [class*="LeftMenu-drawerClose"]',
      )
      if (!drawer) {
        setLeftMenuWidth(LEFT_MENU_WIDTH_COLLAPSED)
        return
      }

      const isOpen = drawer.classList.toString().includes('drawerOpen')
      const newWidth = isOpen
        ? LEFT_MENU_WIDTH_EXPANDED
        : LEFT_MENU_WIDTH_COLLAPSED

      setLeftMenuWidth(newWidth)
    }

    // Update on resize events (left menu dispatches resize when expanding/collapsing)
    window.addEventListener('resize', updateMenuWidth)

    // Use MutationObserver to detect class changes on the drawer
    const drawer = document.querySelector(
      '[class*="LeftMenu-drawer"]',
    )?.parentElement

    let observer: MutationObserver | null = null
    if (drawer) {
      observer = new MutationObserver(updateMenuWidth)
      observer.observe(drawer, {
        attributes: true,
        attributeFilter: ['class'],
        subtree: true,
      })
    }

    return () => {
      window.removeEventListener('resize', updateMenuWidth)
      observer?.disconnect()
    }
  }, [])

  return leftMenuWidth
}
